import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaAngleDown, FaAngleUp, FaUserCircle, FaThLarge, FaSignOutAlt } from 'react-icons/fa';
import './Header.css';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openSubMenus, setOpenSubMenus] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setIsLoggedIn(true);
        }

        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMenuOpen(false);
        setOpenSubMenus([]);
    }, [location.pathname]);

    // Prevent body scrolling when mobile drawer is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    const handleToggleNav = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleToggleSubMenu = (menu) => {
        if (openSubMenus.includes(menu)) {
            setOpenSubMenus(openSubMenus.filter(item => item !== menu));
        } else {
            setOpenSubMenus([...openSubMenus, menu]);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        navigate('/Userlogin');
    };

    const isAboutActive = ['/Ourstory', '/Missionvission', '/Corevalues', '/Expertise'].includes(location.pathname);
    const isProductsActive = ['/Overview', '/Featurebenifits', '/Pricingplan', '/Requestdemo'].includes(location.pathname);
    const isResourcesActive = ['/Blog', '/Casestudy', '/Userguide'].includes(location.pathname);

    return (
        <header className={`nd-header ${isScrolled ? 'nd-header-scrolled' : ''}`}>
            <div className="container nd-header-container">
                <div className="nd-navbar-wrap">
                    {/* Brand Logo */}
                    <div className="nd-brand-logo">
                        <Link to="/" className="nd-logo-link">
                            <img
                                src="../assets/images/icon/logo01.png"
                                alt="ND Connect Logo"
                                className="img-fluid logo-img"
                            />
                        </Link>
                    </div>

                    {/* Navigation */}
                    <nav className="nd-nav">
                        {/* Mobile Toggle Button */}
                        <button 
                            type="button"
                            className="nd-toggle-btn" 
                            onClick={handleToggleNav}
                            aria-label="Toggle navigation"
                        >
                            <FaBars />
                        </button>

                        {/* Mobile Backdrop */}
                        <div 
                            className={`nd-backdrop ${isMenuOpen ? 'open' : ''}`} 
                            onClick={handleToggleNav} 
                        />

                        {/* Nav Items List */}
                        <ul className={`nd-nav-list ${isMenuOpen ? 'open' : ''}`}>
                            {/* Mobile Drawer Top Header */}
                            <li className="nd-drawer-header">
                                <div className="nd-brand-drawer">
                                    <img src="../assets/images/icon/logo01.png" alt="ND Connect" style={{ height: 34 }} />
                                </div>
                                <button type="button" className="nd-close-btn" onClick={handleToggleNav}>
                                    <span>Close</span>
                                    <FaTimes className="ms-1" />
                                </button>
                            </li>

                            {/* Home */}
                            <li className="nd-nav-item">
                                <Link to="/" className={`nd-menu-title ${location.pathname === '/' ? 'active-link' : ''}`}>
                                    <span>Home</span>
                                </Link>
                            </li>

                            {/* About Us */}
                            <li className="nd-nav-item nd-dropdown-item">
                                <button
                                    type="button"
                                    className={`nd-menu-title nd-dropdown-btn ${isAboutActive ? 'active-link' : ''}`}
                                    onClick={() => handleToggleSubMenu('about-us')}
                                >
                                    <span>About Us</span>
                                    <FaAngleDown className="chevron-icon ms-1" />
                                </button>

                                <ul className={`nd-dropdown-card ${openSubMenus.includes('about-us') ? 'show' : ''}`}>
                                    <li>
                                        <Link to="/Ourstory" className={location.pathname === '/Ourstory' ? 'active-sublink' : ''}>Our Story</Link>
                                    </li>
                                    <li>
                                        <Link to="/Missionvission" className={location.pathname === '/Missionvission' ? 'active-sublink' : ''}>Mission & Vision</Link>
                                    </li>
                                    <li>
                                        <Link to="/Corevalues" className={location.pathname === '/Corevalues' ? 'active-sublink' : ''}>Our Core Values</Link>
                                    </li>
                                    <li>
                                        <Link to="/Expertise" className={location.pathname === '/Expertise' ? 'active-sublink' : ''}>Our Expertise</Link>
                                    </li>
                                </ul>
                            </li>

                            {/* Products & Services */}
                            <li className="nd-nav-item nd-dropdown-item">
                                <button
                                    type="button"
                                    className={`nd-menu-title nd-dropdown-btn ${isProductsActive ? 'active-link' : ''}`}
                                    onClick={() => handleToggleSubMenu('products-services')}
                                >
                                    <span>Products & Services</span>
                                    <FaAngleDown className="chevron-icon ms-1" />
                                </button>
                                <ul className={`nd-dropdown-card ${openSubMenus.includes('products-services') ? 'show' : ''}`}>
                                    <li>
                                        <Link to="/Overview" className={location.pathname === '/Overview' ? 'active-sublink' : ''}>Overview</Link>
                                    </li>
                                    <li>
                                        <Link to="/Featurebenifits" className={location.pathname === '/Featurebenifits' ? 'active-sublink' : ''}>Features & Benefits</Link>
                                    </li>
                                    <li>
                                        <Link to="/Pricingplan" className={location.pathname === '/Pricingplan' ? 'active-sublink' : ''}>Pricing Plans</Link>
                                    </li>
                                    <li>
                                        <Link to="/Requestdemo" className={location.pathname === '/Requestdemo' ? 'active-sublink' : ''}>Request a Demo</Link>
                                    </li>
                                </ul>
                            </li>

                            {/* Resources */}
                            <li className="nd-nav-item nd-dropdown-item">
                                <button
                                    type="button"
                                    className={`nd-menu-title nd-dropdown-btn ${isResourcesActive ? 'active-link' : ''}`}
                                    onClick={() => handleToggleSubMenu('resources')}
                                >
                                    <span>Resources</span>
                                    <FaAngleDown className="chevron-icon ms-1" />
                                </button>
                                <ul className={`nd-dropdown-card ${openSubMenus.includes('resources') ? 'show' : ''}`}>
                                    <li>
                                        <Link to="/Blog" className={location.pathname === '/Blog' ? 'active-sublink' : ''}>Blog</Link>
                                    </li>
                                    <li>
                                        <Link to="/Casestudy" className={location.pathname === '/Casestudy' ? 'active-sublink' : ''}>Case Studies</Link>
                                    </li>
                                    <li>
                                        <Link to="/Userguide" className={location.pathname === '/Userguide' ? 'active-sublink' : ''}>User Guide</Link>
                                    </li>
                                </ul>
                            </li>

                            {/* Other Services */}
                            <li className="nd-nav-item">
                                <Link to="/Otherservices" className={`nd-menu-title ${location.pathname === '/Otherservices' ? 'active-link' : ''}`}>
                                    <span>Other Services</span>
                                </Link>
                            </li>

                            {/* Contact Us */}
                            <li className="nd-nav-item">
                                <Link to="/Contactus" className={`nd-menu-title ${location.pathname === '/Contactus' ? 'active-link' : ''}`}>
                                    <span>Contact Us</span>
                                </Link>
                            </li>

                            {/* Account / Login */}
                            {isLoggedIn ? (
                                <li className="nd-nav-item nd-dropdown-item nd-user-dropdown">
                                    <button 
                                        type="button" 
                                        className="nd-user-badge" 
                                        onClick={() => handleToggleSubMenu('user-account')}
                                    >
                                        <FaUserCircle className="me-1" />
                                        <span className="user-text">Account</span>
                                        <FaAngleDown className="chevron-icon ms-1" />
                                    </button>
                                    <ul className={`nd-dropdown-card nd-user-menu ${openSubMenus.includes('user-account') ? 'show' : ''}`}>
                                        <li>
                                            <Link to="/Dashboards"><FaThLarge className="me-2" />Dashboard</Link>
                                        </li>
                                        <li onClick={handleLogout}>
                                            <Link to="#"><FaSignOutAlt className="me-2 text-danger" />Logout</Link>
                                        </li>
                                    </ul>
                                </li>
                            ) : (
                                <li className="nd-nav-item nd-action-item">
                                    <Link to="/Userlogin" className="nd-login-pill">
                                        <FaUserCircle className="login-icon" />
                                        <span>Login</span>
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default Header;
